// src/components/SearchResultsPanel.jsx
// Reusable search result block used in the Navbar dropdown.
// Behaviour is same as your Home.jsx bottom section:
// - "No exact topic found" + "Add this exact topic" button
// - "Other topics from your sentence" (smart suggestions)
// - "Showing X of Y results for"
// - Topic cards list (using SearchCard)

import SearchCard from "./SearchCard";

// Common small words we want to ignore for suggestions
const STOP_WORDS = [
  "in",
  "on",
  "at",
  "the",
  "a",
  "an",
  "of",
  "for",
  "to",
  "is",
  "are",
  "was",
  "were",
  "and",
  "or",
  "me",
  "you",
  "i",
];

function SearchResultsPanel({
  query,
  loading,
  results,
  total,
  hasExactMatch,
  onAddExactTopic,
  onResultClick,
  onSuggestionClick,
  error
}) {
  // STEP 1 — Clean the incoming query string
  const trimmedQuery = (query || "").trim();

  // If nothing to show, don't render the panel at all
  if (!trimmedQuery) return null;

  // STEP 2 — Lowercase version of full query for comparisons
  const normalizedQuery = trimmedQuery.toLowerCase();

  // -----------------------------------------------
  // Build "Smart Suggestions" from user's sentence
  // Example: "mango train in heaven"
  // -> tokens: ["mango", "train", "heaven"]
  // -> we only add tokens that:
  //    - exist as a topic word in `results`
  //    - are not the full query itself
  // -----------------------------------------------
  let suggestedWords = [];

  if (normalizedQuery && results.length > 0) {
    const tokens = normalizedQuery
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t && !STOP_WORDS.includes(t));

    const set = new Set();

    tokens.forEach((token) => {
      const match = results.find(
        (t) => (t.word || "").trim().toLowerCase() === token
      );

      if (
        match &&
        match.word &&
        match.word.trim().toLowerCase() !== normalizedQuery // don't add full-query topic as suggestion
      ) {
        set.add(match.word); // keep original casing (e.g. "Train")
      }
    });

    suggestedWords = Array.from(set);
  }

  return (
  <div className="search-results-panel">
    {/* If API failed: show friendly error instead of empty results */}
    {error && !loading && (
      <p className="search-error">
        {error}
      </p>
    )} 
      {/* Loading state */}
      {loading && <p>Searching topics...</p>}

      {/* NO results at all (but only when there is NO error) */}
     {!loading && !error && results.length === 0 && (
  <div className="no-results">
    {/* Short, direct message – same style as Home.jsx */}
    <p>No topic found — create it now!</p>
  <button
  type="button"
  onClick={onAddExactTopic}
  className="search-add-topic-btn"
>
  Create exact topic
</button>
  </div>
)}
       {/* Smart suggestions from user's sentence
          Only show when:
          - we actually have suggestions AND
          - there is NO exact topic for the full query
          (so UI doesn't feel duplicated) */}
      {suggestedWords.length > 0 && !hasExactMatch && (
        <div className="search-suggestions">
          <p>Other topics from your sentence:</p>
          <div className="search-suggestions-list">
            {suggestedWords.map((w) => (
              <button
                key={w}
                type="button"
                className="suggestion-chip"
                onClick={() =>
                  onSuggestionClick && onSuggestionClick(w)
                }
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* When we DO have some results */}
      {!loading && results.length > 0 && (
        <>
          {/* If there is NO exact topic with this full line,
              show a gentle message + Add Topic button on top */}
          {!hasExactMatch && (
  <div className="no-exact-match">
    <p>No exact topic — create it now!</p>
   <button
  type="button"
  onClick={onAddExactTopic}
  className="search-add-topic-btn subtle"
>
  Create exact topic
</button>
  </div>
)}
     {/* Meta info: how many results */}
          <div className="search-meta">
            <p>
              Showing {results.length} of {total} result
              {total !== 1 ? "s" : ""} for “{trimmedQuery}”.
            </p>
          </div>

          {/* Topic cards list – uses same SearchCard as Home.jsx */}
          <div className="search-results-grid">
            {results.map((topic) => (
              <SearchCard
                key={topic.id}
                topic={topic}
                onClick={() => onResultClick(topic)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default SearchResultsPanel;
